/**
 * TodoWidget — "Mijn taken". Open productietaken van de gebruiker uit Airtable,
 * gesorteerd op prioriteit (Deadline/Prio 1 eerst). Klik → Vandaag; het rondje
 * vinkt de taak af (tool-feedback #24), met ongedaan maken + Ctrl+Z.
 * Databron: GET /Api/my-tasks (zelfde bron als de Vandaag-pagina).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../../utils/ongedaan';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { T } from './widgetTokens';
import { NIET_PRODUCTIE_SET } from '../../../utils/taakStatus';

// Kalender-blokken en afgeronde items zijn geen taken (bekend patroon in dit repo).
const NON_TASK = NIET_PRODUCTIE_SET;
const PRIO_ORDER = { Deadline: 0, 'Prio 1': 1, 'Prio 2': 2, 'Prio 3': 3, 'Prio 4': 4, 'Wacht op feedback': 5, 'Geen prio': 6 };
const PRIO_STYLE = {
  Deadline: { bg: '#fee2e2', color: '#b91c1c' },
  'Prio 1': { bg: '#fee2e2', color: '#b91c1c' },
  'Prio 2': { bg: '#ffedd5', color: '#c2410c' },
  'Prio 3': { bg: '#fef3c7', color: '#b45309' },
  'Prio 4': { bg: '#fef9c3', color: '#a16207' },
  'Wacht op feedback': { bg: '#ffedd5', color: '#c2410c' },
};

const fmtDeadline = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }); }
  catch { return ''; }
};

export default function TodoWidget() {
  const { accounts } = useMsal();
  const userEmail = accounts?.[0]?.username || '';
  const [tasks, setTasks] = useState(null); // null = laden
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    if (!userEmail) { setTasks([]); return undefined; }
    authFetch(`${BACKEND_URL}/Api/my-tasks?email=${encodeURIComponent(userEmail)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('fetch failed'))))
      .then((d) => { if (active) setTasks((d.tasks || []).map((t) => ({ ...(t.fields || {}), _id: t.id }))); })
      .catch(() => { if (active) { setError(true); setTasks([]); } });
    return () => { active = false; };
  }, [userEmail]);

  useEffect(() => registreerOngedaanSneltoets(), []);

  // Zelfde schrijfweg als de Vandaag-pagina en de volledige cockpit: PrioNr + Afgerond.
  const zetAfgerond = useCallback(async (taak, afgerond, prio) => {
    const r = await authFetch(`${BACKEND_URL}/Api/tasks/${taak._id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ PrioNr: prio, Afgerond: afgerond }),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    setTasks((prev) => (prev || []).map((f) => (f._id === taak._id ? { ...f, Afgerond: afgerond, PrioNr: prio } : f)));
  }, []);

  const vinkAf = useCallback(async (taak) => {
    if (!taak._id) return;
    const vorigePrio = taak.PrioNr || 'Geen prio';
    try {
      await zetAfgerond(taak, true, 'Afgerond');
      biedOngedaanAan(`"${taak.NaamTaak || 'Taak'}" afgerond`, () => zetAfgerond(taak, false, vorigePrio));
    } catch {
      toast.error('Kon taak niet afvinken');
    }
  }, [zetAfgerond]);

  const shown = useMemo(() => {
    return (tasks || [])
      .filter((f) => !f.Afgerond && !NON_TASK.has(f.PrioNr))
      .sort((a, b) => (PRIO_ORDER[a.PrioNr] ?? 9) - (PRIO_ORDER[b.PrioNr] ?? 9))
      .slice(0, 8);
  }, [tasks]);

  if (tasks === null) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={18} sx={{ color: T.outlineVariant }} /></Box>;
  }
  if (error) {
    return <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline }}>Taken konden niet geladen worden.</Typography>;
  }
  if (shown.length === 0) {
    return <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline }}>Geen open taken — lekker bezig! 🎉</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {shown.map((f, i) => {
        const st = PRIO_STYLE[f.PrioNr] || { bg: T.surfaceLow, color: T.secondary };
        const deadline = fmtDeadline(f.Deadline);
        return (
          <Box key={f._id || i} sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            bgcolor: T.surfaceLow, borderRadius: '0.5rem', py: 0.5, pr: 1.25, pl: 0.5, borderLeft: `3px solid ${st.color}`,
            transition: 'background 0.15s', '&:hover': { bgcolor: T.surfaceHigh },
          }}>
            <Tooltip title="Afvinken" arrow>
              <IconButton size="small" aria-label={`${f.NaamTaak || 'Taak'} afvinken`} onClick={() => vinkAf(f)}
                sx={{ color: T.outlineVariant, '&:hover': { color: T.emerald }, '& .vol': { display: 'none' }, '&:hover .leeg': { display: 'none' }, '&:hover .vol': { display: 'inline-block' } }}>
                <RadioButtonUncheckedIcon className="leeg" sx={{ fontSize: 20 }} />
                <CheckCircleIcon className="vol" sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Box component="a" href="/vandaag" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0, textDecoration: 'none', py: 0.75 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 600, color: T.text }}>
                {f.NaamTaak || 'Naamloze taak'}
              </Typography>
              {(() => {
                // Lookup-veld uit Airtable is een array (bekend patroon).
                const proj = f['Projectnaam (from Project)'];
                const projStr = Array.isArray(proj) ? proj.join(', ') : (proj || '');
                if (!projStr && !deadline) return null;
                return (
                  <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline, mt: 0.15 }}>
                    {[projStr, deadline ? `deadline ${deadline}` : ''].filter(Boolean).join(' · ')}
                  </Typography>
                );
              })()}
            </Box>
            {f.PrioNr && (
              <Typography sx={{
                fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 700, flexShrink: 0,
                bgcolor: st.bg, color: st.color, px: 1, py: 0.3, borderRadius: '9999px',
              }}>
                {f.PrioNr}
              </Typography>
            )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
